import React from "react";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PremiumInput from "../../../../Components/Inputs/PremiumInput";
import PremiumSelect from "../../../../Components/Inputs/PremiumSelect";
import { showGlobalToast } from "../../../../Components/ToastProvider";

const JOB_POSITION_OPTIONS = [
  { value: "frontend_developer", label: "توسعه‌دهنده فرانت‌اند" },
  { value: "backend_developer", label: "توسعه‌دهنده بک‌اند" },
  { value: "fullstack_developer", label: "توسعه‌دهنده فول‌استک" },
  { value: "mobile_developer", label: "توسعه‌دهنده موبایل" },
  { value: "devops_engineer", label: "مهندس دواپس" },
  { value: "qa_engineer", label: "کارشناس تضمین کیفیت" },
  { value: "data_scientist", label: "دانشمند داده" },
  { value: "data_engineer", label: "مهندس داده" },
  { value: "ai_engineer", label: "مهندس هوش مصنوعی" },
  { value: "product_manager", label: "مدیر محصول" },
  { value: "ui_ux_designer", label: "طراح رابط و تجربه کاربری" },
  { value: "scrum_master", label: "اسکرام مستر" },
  { value: "digital_marketing", label: "کارشناس دیجیتال مارکتینگ" },
  { value: "seo_specialist", label: "متخصص سئو" },
  { value: "content_creator", label: "کارشناس تولید محتوا" },
  { value: "sales_expert", label: "کارشناس فروش" },
  { value: "customer_support", label: "کارشناس پشتیبانی" },
  { value: "hr_specialist", label: "کارشناس منابع انسانی" },
  { value: "administrative", label: "مسئول دفتر / امور اداری" },
  { value: "other", label: "سایر" },
];

const WORK_TYPE_OPTIONS = [
  { value: "remote", label: "دورکاری" },
  { value: "onsite", label: "حضوری" },
  { value: "hybrid", label: "هیبرید (ترکیبی)" },
];

const JOB_FIELDS = [
  {
    name: "position_applied",
    label: "موقعیت شغلی مورد نظر",
    size: { xs: 12, md: 6 },
    select: true,
    options: JOB_POSITION_OPTIONS,
  },
  {
    name: "work_type",
    label: "نوع همکاری",
    size: { xs: 12, md: 6 },
    select: true,
    options: WORK_TYPE_OPTIONS,
  },
  {
    name: "years_of_experience",
    label: "سال‌های سابقه کار",
    type: "number",
    size: { xs: 12, md: 6 },
    placeholder: "مثال: 5",
  },
  {
    name: "expected_salary",
    label: "حقوق درخواستی (تومان)",
    type: "number",
    size: { xs: 12, md: 6 },
    placeholder: "مثال: 25000000",
  },
  {
    name: "familiarity_source",
    label: "نحوه آشنایی با ما (اختیاری)",
    size: { xs: 12, md: 6 },
    placeholder: "مثال: از طریق دوستان، اینستاگرام",
  },
  {
    name: "linkedin_url",
    label: "لینکدین (اختیاری)",
    size: { xs: 12, md: 6 },
    placeholder: "https://linkedin.com/in/yourprofile",
  },
  {
    name: "portfolio_url",
    label: "پورتفولیو (اختیاری)",
    size: { xs: 12, md: 6 },
    placeholder: "https://yourportfolio.com",
  },
  {
    name: "cover_letter",
    label: "توضیحات تکمیلی (کاور لتر)",
    size: { xs: 12 },
    multiline: true,
    rows: 4,
  },
];

export default function JobInfoSection({ formik }) {
  const skills = formik?.values?.skills ?? [];

  const handleAddSkill = (value) => {
    const currentSkills = formik?.values?.skills ?? [];
    if (!currentSkills.includes(value)) {
      formik?.setFieldValue("skills", [...currentSkills, value]);
    } else {
      showGlobalToast("این مهارت قبلاً اضافه شده است", "warning");
    }
  };

  return (
    <>
      <Typography
        sx={{ color: "#00ffcc", fontWeight: 700, mb: 2, mt: 4 }}
        component="h2"
      >
        اطلاعات شغلی و تخصصی
      </Typography>

      <Grid container>
        {JOB_FIELDS.map((field, index) => {
          if (field?.select) {
            return (
              <Grid size={field?.size} key={`job-${index}`} p={1}>
                <PremiumSelect
                  formik={formik}
                  name={field?.name}
                  label={field?.label}
                  options={field?.options}
                />
              </Grid>
            );
          }
          return (
            <Grid size={field?.size} key={`job-${index}`} p={1}>
              <PremiumInput
                formik={formik}
                name={field?.name}
                label={field?.label}
                type={field?.type || "text"}
                multiline={field?.multiline || false}
                rows={field?.rows}
                placeholder={field?.placeholder}
              />
            </Grid>
          );
        })}
      </Grid>

      <Grid size={{ xs: 12 }} p={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              fontWeight: 500,
            }}
            component="label"
          >
            مهارت‌ها
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: 12 }}>
            {skills?.length} مهارت
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
            minHeight: "45px",
            p: 1.5,
            bgcolor: "rgba(0,0,0,0.15)",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.06)",
            alignItems: "center",
          }}
        >
          {skills?.length > 0 ? (
            skills.map((skill, index) => (
              <Box
                key={index}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "rgba(0,255,204,0.12)",
                  color: "#00ffcc",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "20px",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid rgba(0,255,204,0.2)",
                  "&:hover .remove-skill": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                }}
              >
                <span>#{skill}</span>
                <IconButton
                  className="remove-skill"
                  size="small"
                  onClick={() => {
                    const newSkills = skills.filter((_, i) => i !== index);
                    formik?.setFieldValue("skills", newSkills);
                  }}
                  sx={{
                    p: 0,
                    m: 0,
                    color: "#00ffcc",
                    opacity: 0.5,
                    transform: "scale(0.8)",
                    transition: "all 0.2s ease",
                    "&:hover": { color: "#ff4444", bgcolor: "transparent" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.2)",
                fontSize: 13,
                fontStyle: "italic",
                width: "100%",
                textAlign: "center",
              }}
            >
              هنوز مهارتی اضافه نشده است
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="مثال: React.js"
            id="skill-input"
            onKeyDown={(e) => {
              if (e?.key === "Enter") {
                e.preventDefault();
                const value = e?.target?.value?.trim();
                if (value) {
                  handleAddSkill(value);
                  e.target.value = "";
                }
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "rgba(0,0,0,0.2)",
                "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&.Mui-focused fieldset": {
                  borderColor: "#00ffcc",
                  boxShadow: "0 0 0 3px rgba(0,255,204,0.1)",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
                py: 1.2,
                "&::placeholder": { color: "rgba(255,255,255,0.3)" },
              },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ ml: "8px" }} />}
            onClick={() => {
              const input = document.getElementById("skill-input");
              const value = input?.value?.trim();
              if (value) {
                handleAddSkill(value);
                input.value = "";
              }
            }}
            sx={{
              borderRadius: "12px",
              color: "#00ffcc",
              borderColor: "rgba(0,255,204,0.3)",
              px: 3,
              minWidth: "120px",
              "&:hover": {
                borderColor: "#00ffcc",
                bgcolor: "rgba(0,255,204,0.08)",
              },
            }}
          >
            افزودن
          </Button>
        </Box>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.38)",
            fontSize: 11,
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span
            style={{
              display: "inline-block",
              backgroundColor: "rgba(255,255,255,0.05)",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: 10,
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            Enter
          </span>
          <span>برای افزودن سریع مهارت</span>
        </Typography>
      </Grid>
    </>
  );
}
